import LaunchIcon from "@mui/icons-material/Launch";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Alert,
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Switch,
  Tab,
  Tabs,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNotifications } from "@toolpad/core";
import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useColorMode } from "../../provider/ThemeProviderEx";
import axiosInstance from "../../services/axiosInstance";
import AppFooter from "../AppFooter";
import ProfileMenu from "../ProfileMenu";
import ModalWindow from "../modal/ModalWindow";

/* --- jednotná obálka pro navigační záložku --- */
function LinkTab(props) {
  return (
    <Tab
      component={Link}
      disableRipple
      {...props}
      sx={{
        textTransform: "none",
        fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
        minWidth: 80,
      }}
    />
  );
}

export default function AppLayout({ tabConfig }) {
  const location = useLocation();
  const { mode, toggleMode } = useColorMode();
  const theme = useTheme();
  const notifications = useNotifications();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  /* --- vyhodnocení aktivní záložky --- */
  const tabPaths = tabConfig.map((t) => t.path);
  const currentIndex = tabPaths.indexOf(location.pathname);
  const tabValue = currentIndex === -1 ? false : currentIndex;

  /* --- lokální stavy --- */
  const [welcomeVisible, setWelcomeVisible] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleNavodDownload = async () => {
    try {
      const response = await axiosInstance.get("/api/config/navod", {
        responseType: "blob",
      });
      const pdfUrl = URL.createObjectURL(
        new Blob([response.data], { type: "application/pdf" })
      );
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = "navod.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60_000);
    } catch (error) {
      notifications.show("Chyba při stahování návodu.", {
        severity: "error",
        autoHideDuration: 5000,
      });
    }
  };

  useEffect(() => {
    if (location.state?.showWelcomeModal) setWelcomeVisible(true);
  }, [location.state]);

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
        color: "text.primary",
      })}
    >
      <AppBar position="static">
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: 64 },
            px: { xs: 1, sm: 2 },
            display: "flex",
            position: "relative",
            alignItems: "center",
          }}
        >
          {/* === Hamburger === */}
          {!isDesktop ? (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          ) : (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                gap: 2,
              }}
            >
              <Tabs
                value={tabValue}
                indicatorColor="secondary"
                sx={{
                  maxWidth: "100%", // ← nepřetáhne flexbox
                  "& .MuiTab-root": { color: "white" },
                  "& .MuiTab-root.Mui-selected": { color: "white" },
                  "& .MuiTabs-indicator": { backgroundColor: "white" },
                }}
              >
                {tabConfig.map((tab, idx) =>
                  tab.label === "Návod" ? (
                    <Tab
                      key={tab.label}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          Návod
                          <LaunchIcon sx={{ fontSize: 18, ml: 0.5 }} />
                        </Box>
                      }
                      onClick={handleNavodDownload}
                      sx={{ cursor: "pointer" }}
                    />
                  ) : (
                    <LinkTab
                      key={idx}
                      label={tab.label}
                      to={tab.path}
                      value={idx}
                    />
                  )
                )}
              </Tabs>

              {/* ProfileMenu hned za Tabs */}
              <ProfileMenu />
            </Box>
          )}

          {/* === Přepínač motivu – vždy vpravo === */}
          <Box
            sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}
          >
            <Typography component="span">☀️</Typography>
            <Switch checked={mode === "dark"} onChange={toggleMode} />
            <Typography component="span">🌙</Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* === DRAWER – mobilní navigace === */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
      >
        {/* ProfileMenu (mobily) */}
        <Box sx={{ p: 2, pb: 0 }}>
          <ProfileMenu isInDrawer={true} />
        </Box>
        <List sx={{ width: 240 }}>
          {tabConfig.map((tab, idx) =>
            tab.label === "Návod" ? (
              <ListItemButton
                key={idx}
                onClick={() => {
                  setDrawerOpen(false);
                  handleNavodDownload();
                }}
              >
                <ListItemText primary={tab.label} />
              </ListItemButton>
            ) : (
              <ListItemButton
                key={idx}
                component={Link}
                to={tab.path}
                onClick={() => setDrawerOpen(false)}
                selected={tabValue === idx}
              >
                <ListItemText primary={tab.label} />
              </ListItemButton>
            )
          )}
        </List>
      </Drawer>

      {/* === WELCOME MODAL === */}
      <ModalWindow visible={welcomeVisible} setVisible={setWelcomeVisible}>
        <h3 style={{ margin: "0 auto", textAlign: "center" }}>
          Registrace dokončena
        </h3>
        <Alert severity="info" sx={{ mt: 2 }}>
          Registrace proběhla úspěšně. Odečty můžete zadávat ihned. Pokud
          některá data zatím nevidíte, budou k dispozici následující den.
        </Alert>
      </ModalWindow>

      {/* === HLAVNÍ OBSAH === */}
      <Box
        sx={{
          flex: 1,
          p: { xs: 1, sm: 2 },
          maxWidth: { xs: "90%", lg: "1200px" },
          width: "100%",
          boxSizing: "border-box",
          mx: "auto",
          textAlign: "center",
        }}
      >
        <Outlet />
      </Box>

      <AppFooter sx={{ minHeight: "10vh" }} />
    </Box>
  );
}
